;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { CheckIcon } from '@heroicons/react/20/solid'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Slottable } from '@radix-ui/react-slot'
import { ComponentProps } from 'react'

import { cn } from '../../utils/cn'
import { Icon, IconName } from './icon'

const SELECT_PLACEHOLDER_VALUE = 'SELECT_PLACEHOLDER_VALUE' as const

/**
 * Select based on ShadCN's Select component
 *
 * @see https://ui.shadcn.com/docs/components/select
 */
function Select({
  onValueChange,
  ...props
}: ComponentProps<typeof SelectPrimitive.Root>) {
  return (
    <SelectPrimitive.Root
      {...props}
      onValueChange={(value) =>
        onValueChange?.(value === SELECT_PLACEHOLDER_VALUE ? '' : value)
      }
    />
  )
}

const SelectGroup = SelectPrimitive.Group

/**
 * To avoid google translate issues, make sure to
 * pass a key prop that changes with the value.
 *
 * See discussion about it here:
 * https://qogita.slack.com/archives/C02MC2CNCF8/p1707898203251589?thread_ts=1707846575.653969&cid=C02MC2CNCF8
 *
 * ```
 * <SelectValue key={field.value} />
 * ```
 */

function SelectValue({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Value>) {
  return (
    <SelectPrimitive.Value
      className={cn(
        'text-light overflow-hidden text-nowrap text-ellipsis',
        className,
      )}
      {...props}
    />
  )
}

function SelectTrigger({
  className,
  children,
  iconName = 'heroicons/outline/chevron-up-down',
  ...restProps
}: ComponentProps<typeof SelectPrimitive.Trigger> & {
  iconName?: IconName
}) {
  return (
    <SelectPrimitive.Trigger
      translate="no"
      className={cn(
        [
          'flex w-full min-w-0 items-center justify-between gap-2 rounded-sm border border-gray-300 bg-white text-base font-light whitespace-nowrap text-gray-900 transition-colors [&>span]:line-clamp-1',
          'h-10 px-3 py-2 pr-2 pl-3',
          'data-placeholder:text-base data-placeholder:text-gray-500',
          'disabled:border-gray-200 disabled:text-gray-400 disabled:placeholder-gray-400 disabled:opacity-50',
          'aria-invalid:border-error-700',
          'placeholder:text-gray-500 data-placeholder:text-gray-500 data-[state=open]:z-10 data-[state=open]:border-gray-300',
        ],
        className,
      )}
      {...restProps}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <Icon name={iconName} className="h-4 w-4 shrink-0 text-gray-400" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

/**
 * A trigger that renders as its child instead of its own styled button, for
 * callers that need the trigger to be an existing component (a tab, a button).
 *
 * Kept separate from `SelectTrigger` so the default trigger, and every Select
 * already using it, is unaffected. `Slottable` is required because the trigger
 * renders the caller's element *and* an icon, and `asChild` needs to know which
 * of the two to merge onto.
 */
function SelectTriggerAsChild({
  children,
  ...restProps
}: ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger translate="no" asChild {...restProps}>
      {/* Slottable marks which child Slot merges onto. Unnecessary today since
       * this renders one child, but it keeps `asChild` working if an icon or
       * other sibling is ever added, which is what breaks the default trigger. */}
      <Slottable>{children}</Slottable>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  /**
   * Popper makes the select content appear under the trigger input.
   *
   * Visual Example:
   *   Label:    Pet
   *   Trigger:  [Select a pet]
   *   Dropdown:
   *     [Cat]
   *     [Dog]
   *     [Hamster]
   *     [Parrot]
   */

  position = 'popper',
  className,
  children,
  ...restProps
}: ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=closed]:zoom-out-95 motion-safe:data-[state=open]:zoom-in-95 motion-safe:data-[side=bottom]:slide-in-from-top-2 motion-safe:data-[side=left]:slide-in-from-right-2 motion-safe:data-[side=right]:slide-in-from-left-2 motion-safe:data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-60 min-w-32 overflow-auto rounded-md border border-gray-300 bg-white text-sm text-gray-900 shadow-lg',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...restProps}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'p-0.5',
            position === 'popper' &&
              'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectItem({
  className,
  children,
  ...restProps
}: ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        'font-outfit relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-8 pl-3 text-sm font-light outline-hidden select-none focus:bg-gray-800 focus:text-white data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
      {...restProps}
    >
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>
        <span>{children}</span>
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

/**
 * Use this to add an empty item to the select. It will force the Select to set
 * the value to empty string when the item is selected.
 */
function SelectEmptyItem(
  props: Omit<ComponentProps<typeof SelectPrimitive.Item>, 'value'>,
) {
  return <SelectItem {...props} value={SELECT_PLACEHOLDER_VALUE} />
}

function SelectSeparator({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      className={cn('-mx-1 my-1 h-px bg-gray-300', className)}
      {...props}
    />
  )
}

export {
  Select,
  SelectContent,
  SelectEmptyItem,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectTriggerAsChild,
  SelectValue,
}
